import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import {
  openTicketsState,
  closedTicketsState,
  fetchingOpenState,
  fetchingClosedState,
  userPreferencesState,
  setUserPreferences,
  currentViewNameState,
  allTicketsState,
} from './TicketListSlice';
import { startLoading, stopLoading } from '../ContentTabs/ContentTabsSlice';
import { userInfoState } from '../HomeSlice';
import TicketsTable from '../../TicketsTable/TicketsTable';
import ApiService from '../../../services/ApiService';

const headCells = [
  { id: 'ticketNumber', numeric: true, label: 'Ticket ID', mandatory: true },
  { id: 'wagonNumber', numeric: true, label: 'Wagon', mandatory: true },
  {
    id: 'ticketCreator',
    numeric: false,
    label: 'Ticket creator',
    mandatory: true,
  },
  { id: 'plannedType', numeric: false, label: 'Type', mandatory: false },
  {
    id: 'ticketStatus',
    numeric: false,
    label: 'Ticket status',
    mandatory: false,
  },
  {
    id: 'businessUnit',
    numeric: false,
    label: 'Business unit',
    mandatory: false,
  },
  {
    id: 'lastAnomalyDate',
    numeric: false,
    label: 'Last anomaly',
    mandatory: false,
  },
  {
    id: 'wagonStatus',
    numeric: false,
    label: 'Wagon status',
    mandatory: false,
  },
  {
    id: 'loadedStatus',
    numeric: false,
    label: 'Loaded status',
    mandatory: true,
  },
  { id: 'priority', numeric: true, label: 'Priority', mandatory: false },
  { id: 'damageCodes', numeric: false, label: 'Damage codes', mandatory: true },
  {
    id: 'damagePlace',
    numeric: false,
    label: 'Damage place',
    mandatory: false,
  },
  {
    id: 'siteConcerned',
    numeric: false,
    label: 'Site concerned',
    mandatory: false,
  },
  { id: 'keeper', numeric: false, label: 'Keeper', mandatory: true },
  { id: 'productType', numeric: false, label: 'Product type', mandatory: true },
  { id: 'origin', numeric: false, label: 'Origin', mandatory: false },
  { id: 'labelWagon', numeric: false, label: 'Label wagon', mandatory: true },
  {
    id: 'appliedLabel',
    numeric: false,
    label: 'Applied label',
    mandatory: true,
  },
  {
    id: 'nextRevisionDate',
    numeric: false,
    label: 'Next revision',
    mandatory: false,
  },
  {
    id: 'assignedWorkshop',
    numeric: false,
    label: 'Assigned workshop',
    mandatory: false,
  },
];

export default function TicketListContent(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = i18n.language;
  const { selectedTicketType, formatUserPrefs } = props;
  const fetchingOpen = useSelector(fetchingOpenState);
  const fetchingClosed = useSelector(fetchingClosedState);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [defaultTicketList, setDefaultTicketList] = React.useState([]);
  const openTickets = useSelector(openTicketsState);
  const closedTickets = useSelector(closedTicketsState);
  const allTickets = useSelector(allTicketsState);
  const [rowsFiltered, setRowsFiltered] = React.useState(defaultTicketList);
  const userPreferences = useSelector(userPreferencesState);
  const currentViewName = useSelector(currentViewNameState);
  const user = useSelector(userInfoState);

  function updateRowsFiltered() {
    let result = defaultTicketList;
    const { rowsFilters } = userPreferences.find(
      (userPreference) => userPreference.viewName === currentViewName,
    );

    rowsFilters.map((item) => {
      if (item.selectedOptions.length > 0) {
        result = result.filter(
          (row) => item.selectedOptions.includes(t(row[item.cell])) === true,
        );
      }
      return true;
    });
    setRowsFiltered(result);
  }

  useEffect(() => {
    const userPreferencesFormatted = userPreferences.map((userPreference) => {
      const pref = {};
      const { viewName } = userPreference;
      const { ticketListFiltered, filteredColumns, rowsFilters } =
        userPreference;
      pref.viewName = viewName;
      pref.ticketListFiltered = ticketListFiltered;
      pref.filteredColumns = filteredColumns;
      pref.rowsFilters = [];
      if (rowsFilters !== undefined) {
        rowsFilters.forEach((key) => {
          const rowsFilter = {};
          rowsFilter.cell = key.cell;
          const translatedSelectedOption = [];
          key.selectedOptions.forEach((selectedOption) =>
            translatedSelectedOption.push(t(selectedOption)),
          );
          rowsFilter.selectedOptions = translatedSelectedOption;
          pref.rowsFilters.push(rowsFilter);
        });
      }
      return pref;
    });
    dispatch(setUserPreferences(userPreferencesFormatted));
  }, [lang]);

  function checkReplaciblePrefrence(newPrefrence) {
    const oldValue = userPreferences.find(
      (p) => newPrefrence.viewName === p.viewName,
    );
    if (oldValue) {
      const bothSame =
        Array.isArray(oldValue.rowsFilters) &&
        Array.isArray(newPrefrence.rowsFilters) &&
        oldValue.rowsFilters.length === newPrefrence.rowsFilters.length &&
        oldValue.rowsFilters.every(
          (val, index) => val === newPrefrence.rowsFilters[index],
        );

      if (!bothSame && currentViewName === newPrefrence.viewName) {
        newPrefrence.rowsFilters = oldValue.rowsFilters;
      }
    }
  }

  useEffect(() => {
    ApiService.getUserPreferences().then((response) => {
      const userPreferencesFormatted = formatUserPrefs(response.data);
      userPreferencesFormatted.forEach((prefrence) => {
        if (
          !(
            prefrence.viewName === 'open tickets' ||
            prefrence.viewName === 'closed tickets' ||
            prefrence.viewName === 'tickets'
          )
        ) {
          checkReplaciblePrefrence(prefrence);
        }
      });
      dispatch(setUserPreferences(userPreferencesFormatted));
    });
  }, []);

  useEffect(() => {
    let ticketListState;
    if (user.internal || user.hasEmilyDRights || user.externalMax) {
      ticketListState =
        selectedTicketType === 'openTicketList' ? openTickets : closedTickets;
    } else {
      ticketListState = allTickets;
    }
    setDefaultTicketList(ticketListState);
  }, [selectedTicketType, openTickets, closedTickets, allTickets]);

  useEffect(() => {
    updateRowsFiltered();
    setPage(0);
  }, [defaultTicketList, userPreferences, currentViewName]);

  useEffect(() => {
    if (fetchingOpen || fetchingClosed) {
      dispatch(startLoading());
    } else {
      dispatch(stopLoading());
    }
  }, [fetchingOpen, fetchingClosed]);

  function getRowsPerPage(newRowsPerPage) {
    setRowsPerPage(newRowsPerPage);
  }

  function getPage(newPage) {
    setPage(newPage);
  }
  return (
    <TicketsTable
      headCells={headCells}
      ticketList={defaultTicketList}
      rows={rowsFiltered}
      count={rowsFiltered.length}
      rowsPerPage={rowsPerPage}
      page={page}
      setRowsPerPage={getRowsPerPage}
      setPage={getPage}
    />
  );
}
